/* eslint-disable import/no-cycle */
import { userVar } from "lib/user";
import type {
  Maybe,
  MembershipDurationEnum,
  MembershipType,
  VariantType
} from "@shared/graphql.types";
import { getAnalyticsTraits } from "tracking/utils/context/traits";
import type { PII } from "./types";
import { PIIDefaults } from "./pIIDefaults";
import { getData } from "./dataLayer";

interface MembershipData {
  brand: string;
  category?: MembershipDurationEnum;
  id: string;
  metric7: number;
  name: string;
  price: number;
  variant: Maybe<string>;
}

export const getMembershipData = ({
  segment = "",
  value,
  name,
  price,
  duration
}: MembershipType): MembershipData => {
  return {
    id: value,
    name,
    price,
    metric7: price,
    brand: "Member",
    category: duration,
    variant: segment
  };
};

const priceTypeToPriceKey = {
  "Member Price": "members",
  "Typical Price": "guest"
} as const;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getProductData = (
  {
    sku,
    title = "",
    subTitle = "",
    pricing = { members: 0, typical: 0, guest: 0 },
    brandName = ""
  }: VariantType,
  priceType: "Member Price" | "Typical Price"
) => {
  return {
    id: sku,
    name: title,
    variant: subTitle ?? "",
    price: pricing[priceTypeToPriceKey[priceType]] ?? 0,
    metric7: pricing.typical,
    brand: brandName
  };
};

export const getCategoryFromPath = (
  path: string
): {
  category: string;
  subCategory: string;
  subSubCategory: string;
} => {
  const splitPath = path
    .split("/")
    .filter((item) => item.length && item !== "shop");

  return splitPath.reduce(
    (acc, item, index) => {
      if (index === 0) acc.category = item;
      if (index === 1) acc.subCategory = item;
      if (index === 2) acc.subSubCategory = item;

      return acc;
    },
    {
      category: "",
      subCategory: "",
      subSubCategory: ""
    }
  );
};

export const getNormalisedPhone = (
  phone: string | undefined,
  CC: string | undefined | null
): string => {
  const sanitisedPhone = (phone || "").replace(/[-()\s+]/g, "");

  if (!sanitisedPhone.length) return "";

  if (CC === "GB") return sanitisedPhone.replace(/^0+7/, "447");

  if (CC === "US") return `1${sanitisedPhone.replace(/^0+1/, "")}`;

  return "";
};

export const getNormalisedPostal = (
  postalCode: string | undefined | null,
  CC: string | undefined | null
): string => {
  const sanitisedPostal = (postalCode || "").toLowerCase().replace(/\s/g, "");

  if (!sanitisedPostal.length) return "";

  if (CC === "GB") return sanitisedPostal;

  if (CC === "US") return sanitisedPostal.replace(/^(\d+)-\d+$/, "$1");

  return "";
};

export const getPII = async (): Promise<PII> => {
  const user = userVar();

  if (!user.isLoggedIn) {
    return PIIDefaults;
  }

  const {
    firstName,
    lastName,
    phoneNumber,
    postCode: postalCode
  } = await getAnalyticsTraits();

  return {
    postalCode,
    phoneNumber,
    firstName: (firstName || "").toLowerCase(),
    lastName: (lastName || "").toLowerCase()
  };
};

export const getHashedEmail = async (
  email: string | undefined
): Promise<string | undefined> => {
  if (!email) return;
  try {
    const sanitisedEmail = email?.toLowerCase().trim();
    // eslint-disable-next-line compat/compat
    const encoder = new TextEncoder();
    const data = encoder.encode(sanitisedEmail);
    const hashedEmail = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashedEmail));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const getGTMPurchaseType = (): "Member Price" | "Typical Price" => {
  const user = userVar();

  if (user?.isMember) return "Member Price";
  if (user?.hasMembershipInBag) return "Member Price";

  return "Typical Price";
};

export const pushEventIdToGtmContext = (eventId: string) => {
  if (!window.dataLayer) return;

  window.dataLayer.push({
    ...getData(),
    event_id: eventId
  });
};
